/**
 * The notification catalogue (docs/notifications/implementation-guide.md
 * sections 2, 3 and 9). A notification code is a Core fact a Console user may
 * be told about, fixed here in code because only code sends it - exactly as a
 * claim is fixed in authority/claims.js. Migration 0024 seeds
 * console_notification from this list (devops/test/pg/notifications.test.js
 * holds the two to each other), and a new code arrives with the migration
 * that ships the code sending it.
 *
 * What an administrator may change per code lives in the database
 * (console_notification: enabled, force_email, email_cooldown_minutes) and
 * starts from the defaults here. What they may not is fixed here: the claims
 * a recipient must hold, the audience, the severity, whether the email is
 * locked on, and who raises it.
 *
 * Each entry:
 *
 *   screen          the Console section it belongs to, as console_claim.screen
 *                   names them (plus 'Business Day'), for grouping in the UI
 *   link            the Console screen key (permissions.js SCREEN_RULES) an
 *                   item opens, or 'profile'
 *   severity        INFO | ACTION | WARNING | CRITICAL - display only
 *   audience        ROLES (the subscribing roles), SUBJECT (the one person the
 *                   fact is about), ROLES_AND_SUBJECT
 *   requiredClaims  a recipient through a role must hold one of these (rule 3,
 *                   section 1): nobody is told about what they cannot see
 *   emailBecause    why it is emailed by default - the owner's rule of
 *                   7 Oct 2026 (section 9): APPROVAL (waiting for a checker),
 *                   AUTO_REJECTION (the system refused something), REJECTION
 *                   (a person rejected something), SECURITY (a locked notice
 *                   to its subject) - or null: in-app only by default
 *   emailLocked     the email cannot be switched off or muted (SECURITY only)
 *   cooldown        the default email_cooldown_minutes
 *   enabled         the default enabled (BOOK_CLOSED starts off)
 *   defaultRoles    the subscriptions 0024 seeds, each covered by the role's
 *                   seeded claims; ADMIN only on the very important codes
 *                   (owner, 7 Oct 2026)
 *   raisedBy        notify (a writer's transaction, notify.js), notifyNow (no
 *                   transaction: SQS directly) or trigger (the database:
 *                   ORDERS_REJECTED, section 4.5) - notify() refuses a code it
 *                   does not raise, so each fact has one way in
 *
 * Browser-safe: no Node module, so the Console imports the words from here.
 */

const SEVERITIES = Object.freeze(['INFO', 'ACTION', 'WARNING', 'CRITICAL']);
const AUDIENCES = Object.freeze(['ROLES', 'SUBJECT', 'ROLES_AND_SUBJECT']);
const EMAIL_BECAUSE = Object.freeze(['APPROVAL', 'AUTO_REJECTION', 'REJECTION', 'SECURITY']);
const RAISED_BY = Object.freeze(['notify', 'notifyNow', 'trigger']);

/** How long an in-app item stays in the inbox (owner, 7 Oct 2026). */
export const INBOX_DAYS = 7;

/** Any of the six view claims (authority/claims.js VIEW_CLAIMS), written out: this module imports nothing. */
const ANY_VIEW = Object.freeze(['core.uploads.view', 'core.prices.view', 'core.dealing.view', 'core.registry.view',
  'core.reports.view', 'core.clients.view']);

const CODE = /^[A-Z][A-Z0-9_]{2,63}$/;

/**
 * Build one frozen catalogue entry, refusing a malformed one at module load -
 * so a typo in this file fails every test that imports it, not a dispatch in
 * production. forceEmail and emailLocked are DERIVED from emailBecause, so the
 * owner's email rule has one source: an entry is emailed by default exactly
 * when it says why.
 */
function n(code, {
  screen, link, severity, audience = 'ROLES', requiredClaims, emailBecause = null, cooldown = 0, enabled = true,
  defaultRoles = [], raisedBy = 'notify',
}) {
  // The code doubles as a database key and a template key: upper snake case only.
  if (!CODE.test(code)) throw new Error('not a notification code: ' + code);
  if (!SEVERITIES.includes(severity)) throw new Error(code + ': severity ' + severity);
  if (!AUDIENCES.includes(audience)) throw new Error(code + ': audience ' + audience);
  if (emailBecause !== null && !EMAIL_BECAUSE.includes(emailBecause)) throw new Error(code + ': emailBecause ' + emailBecause);
  if (!RAISED_BY.includes(raisedBy)) throw new Error(code + ': raisedBy ' + raisedBy);
  // Rule 3 (section 1): a code that reaches people through their roles must say which
  // claims a recipient needs, or the claim gate in the recipient query would let anyone in.
  if (audience !== 'SUBJECT' && !requiredClaims.length) throw new Error(code + ': a role audience needs required claims');
  return Object.freeze({
    code,
    screen,
    link,
    severity,
    audience,
    requiredClaims: Object.freeze([...requiredClaims]),
    emailBecause,
    forceEmail: emailBecause !== null,
    emailLocked: emailBecause === 'SECURITY',
    cooldown,
    enabled,
    defaultRoles: Object.freeze([...defaultRoles]),
    raisedBy,
  });
}

/** Section 9, in its order. */
export const NOTIFICATIONS = Object.freeze([
  // --- 9.1 File Uploads --------------------------------------------------------
  // Raised by intake/scan.js applyScanResult when a scanned batch is VALIDATED and is
  // NOT auto-accepted (intake/auto-accept.js isAutoAccepted) - a checker has work to do.
  // Recipients: holders of core.intake.accept, minus the uploader (four eyes would refuse them).
  // Emailed: a waiting approval.
  n('INTAKE_FILE_AWAITING_ACCEPTANCE', {
    screen: 'File Uploads', link: 'uploads', severity: 'ACTION', requiredClaims: ['core.intake.accept'],
    emailBecause: 'APPROVAL', defaultRoles: ['SUPERVISOR'],
  }),
  // Raised by intake/scan.js applyScanResult when GuardDuty finds malware or the file fails
  // its checks. Everyone who works with uploads hears it, and the uploader always does.
  // Emailed: an automatic rejection. ADMIN is seeded because it is CRITICAL.
  n('INTAKE_FILE_QUARANTINED', {
    screen: 'File Uploads', link: 'uploads', severity: 'CRITICAL', audience: 'ROLES_AND_SUBJECT', requiredClaims: ['core.uploads.view'],
    emailBecause: 'AUTO_REJECTION', defaultRoles: ['OPERATOR', 'PRICING', 'SUPERVISOR', 'COMPLIANCE', 'ADMIN'],
  }),
  // Raised by intake/accept-reject.js rejectBatch. Only the uploader (the subject) is told.
  // Emailed: a rejection by a person.
  n('INTAKE_FILE_REJECTED', {
    screen: 'File Uploads', link: 'uploads', severity: 'WARNING', audience: 'SUBJECT', requiredClaims: ['core.uploads.view'],
    emailBecause: 'REJECTION',
  }),
  // Raised by acceptBatch and autoAcceptBatch. The uploader only, in-app: good news needs no email.
  n('INTAKE_FILE_ACCEPTED', {
    screen: 'File Uploads', link: 'uploads', severity: 'INFO', audience: 'SUBJECT', requiredClaims: ['core.uploads.view'],
  }),

  // --- 9.2 Unit Prices ---------------------------------------------------------
  // Raised by pricing/assemble.js right after sql.insertRun writes a CHECKED run.
  // Recipients: holders of core.price.approve, minus the uploader. Emailed: a waiting approval.
  n('PRICE_RUN_AWAITING_APPROVAL', {
    screen: 'Unit Prices', link: 'unitPrices', severity: 'ACTION', requiredClaims: ['core.price.approve'],
    emailBecause: 'APPROVAL', defaultRoles: ['SUPERVISOR'],
  }),
  // Raised by the same place when the run fails a tolerance check (state QUARANTINED).
  // Recipients: whoever can override (COMPLIANCE) or reject (SUPERVISOR) it.
  // Emailed: the system refused the price, an automatic rejection.
  n('PRICE_RUN_QUARANTINED', {
    screen: 'Unit Prices', link: 'unitPrices', severity: 'WARNING', requiredClaims: ['core.price.override', 'core.price.approve'],
    emailBecause: 'AUTO_REJECTION', defaultRoles: ['COMPLIANCE', 'SUPERVISOR'],
  }),
  // Raised by pricing/actions.js approveRun. Information only, in-app.
  n('PRICE_PUBLISHED', {
    screen: 'Unit Prices', link: 'unitPrices', severity: 'INFO', requiredClaims: ['core.prices.view'],
    defaultRoles: ['PRICING', 'OPERATOR'],
  }),
  // Raised by pricing/actions.js rejectRun. PRICING hears it, and so does the uploader (subject),
  // who must upload a corrected file. Emailed: a rejection.
  n('PRICE_RUN_REJECTED', {
    screen: 'Unit Prices', link: 'unitPrices', severity: 'WARNING', audience: 'ROLES_AND_SUBJECT', requiredClaims: ['core.prices.view'],
    emailBecause: 'REJECTION', defaultRoles: ['PRICING'],
  }),

  // --- 9.3 Transactions & Approvals: orders and books --------------------------
  // Raised by fund-orders acceptInstruction after an intake batch ACCEPTED orders - one
  // notification per batch, never one per order. In-app only: review is the maker step, not an approval.
  n('ORDERS_AWAITING_REVIEW', {
    screen: 'Transactions & Approvals', link: 'approval', severity: 'ACTION', requiredClaims: ['core.order.review'],
    defaultRoles: ['OPERATOR'],
  }),
  // Raised by approval/review.js reviewOrders. The caller passes the orders' reviewers as
  // excludeUids: IP-128 refuses them as approvers, so telling them would be noise.
  // Emailed: a waiting approval, at most one email per person per 30 minutes (cooldown).
  n('ORDERS_AWAITING_APPROVAL', {
    screen: 'Transactions & Approvals', link: 'approval', severity: 'ACTION', requiredClaims: ['core.order.approve'],
    emailBecause: 'APPROVAL', cooldown: 30, defaultRoles: ['SUPERVISOR'],
  }),
  // Section 4.5: every order entering REJECTED - the Rejected transactions table's own condition.
  // NOT raised from JavaScript. Migration 0024's trigger on "order" writes it whenever an order
  // enters REJECTED - which is exactly when it appears in the Console's Rejected transactions table.
  // That covers intake refusals, the checker's ORDER_REJECT, ORDER_REJECT_HOLD and the allocation
  // shortfall, and any rejection path added later. Recipients: everyone who can see that table
  // (core.dealing.view), plus the orders' latest reviewers as subjects. Emailed: a rejection.
  n('ORDERS_REJECTED', {
    screen: 'Transactions & Approvals', link: 'approval', severity: 'WARNING', audience: 'ROLES_AND_SUBJECT',
    requiredClaims: ['core.dealing.view'], emailBecause: 'REJECTION', defaultRoles: ['OPERATOR', 'SUPERVISOR', 'COMPLIANCE'],
    raisedBy: 'trigger',
  }),
  // Raised by approval/send-back.js sendBackOrders. The latest reviewer is the subject, so the
  // maker learns their review must be redone. In-app only: a send-back is not a rejection.
  n('ORDERS_SENT_BACK', {
    screen: 'Transactions & Approvals', link: 'approval', severity: 'ACTION', audience: 'ROLES_AND_SUBJECT',
    requiredClaims: ['core.order.review'], defaultRoles: ['OPERATOR'],
  }),
  // Raised by orders/intake.js when an order's outcome is HELD (HOLD_REASON). COMPLIANCE decides
  // whether to release or reject it. In-app only: a hold is not a rejection (yet).
  n('ORDER_HELD', {
    screen: 'Transactions & Approvals', link: 'approval', severity: 'WARNING', requiredClaims: ['core.order.manage'],
    defaultRoles: ['COMPLIANCE'],
  }),
  // Raised by books/close.js when the close rolls unreviewed orders to the next dealing day
  // (REVIEW_ROLL_CODE). In-app only by default; an administrator can switch the email on.
  n('ORDERS_ROLLED_NOT_REVIEWED', {
    screen: 'Transactions & Approvals', link: 'approval', severity: 'WARNING', requiredClaims: ['core.order.review', 'core.book.close'],
    defaultRoles: ['OPERATOR', 'SUPERVISOR'],
  }),
  // Raised by services/fund-orders/src/lateFreeze.js freezeLateOrders.
  // See docs/runbooks/respond-to-orders-frozen-late.md for what the recipients do.
  n('ORDERS_FROZEN_LATE', {
    screen: 'Transactions & Approvals', link: 'approval', severity: 'WARNING', requiredClaims: ['core.order.manage'],
    defaultRoles: ['SUPERVISOR', 'COMPLIANCE'],
  }),
  // Raised by orders/expiry-sweep.js when unfunded orders lapse. In-app information.
  n('ORDERS_EXPIRED', {
    screen: 'Transactions & Approvals', link: 'approval', severity: 'INFO', requiredClaims: ['core.dealing.view'],
    defaultRoles: ['OPERATOR'],
  }),
  // Raised by approval/approve.js when posting fails (POSTING_FAILED). A unit shortfall is NOT
  // this code: it rejects the orders, so the trigger raises ORDERS_REJECTED instead.
  // The approver is the subject. CRITICAL, so ADMIN is seeded.
  // IP-199 (gap I; owner, 19 Oct 2026): emailed - units were not posted and someone
  // must act the same day (migration 0037 sets force_email on the stored row).
  n('ALLOCATION_FAILED', {
    screen: 'Transactions & Approvals', link: 'approval', severity: 'CRITICAL', audience: 'ROLES_AND_SUBJECT',
    requiredClaims: ['core.order.approve'], emailBecause: 'AUTO_REJECTION', defaultRoles: ['SUPERVISOR', 'ADMIN'],
  }),
  // Raised by books/close.js and close-run.js. Starts disabled: it happens every day and would
  // be noise. An administrator can enable it.
  n('BOOK_CLOSED', {
    screen: 'Transactions & Approvals', link: 'approval', severity: 'INFO', requiredClaims: ['core.dealing.view'], enabled: false,
  }),

  // --- 9.4 The business day ----------------------------------------------------
  // Raised by business-day/advance.js and action.js dayAdvance on every stage move.
  // Anyone with a view claim may subscribe; SUPERVISOR is seeded.
  n('BUSINESS_DAY_STAGE_CHANGED', {
    screen: 'Business Day', link: 'businessDay', severity: 'INFO', requiredClaims: ANY_VIEW, defaultRoles: ['SUPERVISOR'],
  }),
  // Raised by business-day/advance.js when day end is due but a blocking fact remains (orders still
  // to decide, statements missing) once the funding window has closed. The check runs every relay
  // tick, so it is a notifyNow message with a FIXED id per day and reason (hooks.dayBlockedMessage):
  // the dispatcher's claim drops every repeat - one notice per day per reason.
  n('BUSINESS_DAY_BLOCKED', {
    screen: 'Business Day', link: 'businessDay', severity: 'WARNING', requiredClaims: ['core.book.close'], defaultRoles: ['SUPERVISOR'],
    raisedBy: 'notifyNow',
  }),
  // Raised by business-day/advance.js when a step fails (the DayStepFailed metric). There is no
  // register write to ride on, so it goes by notifyNow (straight to SQS). CRITICAL: ADMIN seeded.
  n('BUSINESS_DAY_STEP_FAILED', {
    screen: 'Business Day', link: 'businessDay', severity: 'CRITICAL', requiredClaims: ['core.book.close'],
    defaultRoles: ['SUPERVISOR', 'ADMIN'], raisedBy: 'notifyNow',
  }),

  // The day-end analytics feeds (feeds/): raised by the feed runner with no register write to
  // ride on, so by notifyNow. FAILED is a feed that did not arrive; STALE a day whose files a
  // later correction left behind; WAITING_FOR_WM a day held for the wealth-managed holdings file.
  n('FEEDS_FAILED', {
    screen: 'Business Day', link: 'analyticsFeeds', severity: 'CRITICAL', requiredClaims: ['core.feeds.send'],
    defaultRoles: ['SUPERVISOR', 'ADMIN'], raisedBy: 'notifyNow',
  }),
  n('FEEDS_STALE', {
    screen: 'Business Day', link: 'analyticsFeeds', severity: 'WARNING', requiredClaims: ['core.feeds.send'],
    defaultRoles: ['SUPERVISOR'], raisedBy: 'notifyNow',
  }),
  n('FEEDS_WAITING_FOR_WM', {
    screen: 'Business Day', link: 'analyticsFeeds', severity: 'ACTION', requiredClaims: ['core.feeds.send', 'core.intake.upload'],
    defaultRoles: ['SUPERVISOR'], raisedBy: 'notifyNow',
  }),

  // The Day end process (day-end/, IP-164 to IP-173): raised by the Day end state machine's
  // steps, outside a register transaction, so by notifyNow. WAITING is a day not yet priced or
  // with orders still to decide at 18:00 (and again at 22:00); STOPPED a check that found issues,
  // with counts and the run id only; CLIENT_REBUILT the case 1 repairs; STATEMENT_MISSING a day
  // closed without every bank statement. SUPERVISOR and ADMIN are seeded on all four (owner,
  // 9 Oct 2026): a named exception to ADMIN-only-on-very-important (catalogue.test.js).
  n('DAY_END_WAITING', {
    screen: 'Business Day', link: 'businessDay', severity: 'ACTION', requiredClaims: ['core.book.close'],
    defaultRoles: ['SUPERVISOR', 'ADMIN'], raisedBy: 'notifyNow',
  }),
  // IP-199 (gap I; owner, 19 Oct 2026): emailed - the day stays open and no files go
  // until someone acts (migration 0037 sets force_email on the stored row).
  n('DAY_END_STOPPED', {
    screen: 'Business Day', link: 'businessDay', severity: 'CRITICAL', requiredClaims: ['core.book.close'],
    emailBecause: 'AUTO_REJECTION', defaultRoles: ['SUPERVISOR', 'ADMIN'], raisedBy: 'notifyNow',
  }),
  n('DAY_END_CLIENT_REBUILT', {
    screen: 'Business Day', link: 'businessDay', severity: 'WARNING', requiredClaims: ['core.book.close'],
    defaultRoles: ['SUPERVISOR', 'ADMIN'], raisedBy: 'notifyNow',
  }),
  n('DAY_END_STATEMENT_MISSING', {
    screen: 'Business Day', link: 'businessDay', severity: 'WARNING', requiredClaims: ['core.book.close'],
    defaultRoles: ['SUPERVISOR', 'ADMIN'], raisedBy: 'notifyNow',
  }),

  // --- 9.5 Cash and payouts ----------------------------------------------------
  // Raised by cash/import.js after a statement loads with credits the matcher could not match.
  n('STATEMENT_UNMATCHED_LINES', {
    screen: 'Transactions & Approvals', link: 'approval', severity: 'ACTION', requiredClaims: ['core.cash.claim'],
    defaultRoles: ['OPERATOR'],
  }),
  // Raised by cash/import.js when a statement is refused on load: CHAIN_BLOCKED, DUPLICATE or
  // REJECTED (bank_statement_import.state, carried as the 'reason' param). The uploader is the
  // subject. Emailed: an automatic rejection.
  n('STATEMENT_REFUSED', {
    screen: 'Transactions & Approvals', link: 'uploads', severity: 'WARNING', audience: 'ROLES_AND_SUBJECT',
    requiredClaims: ['core.cash.claim'], emailBecause: 'AUTO_REJECTION', defaultRoles: ['OPERATOR', 'SUPERVISOR'],
  }),
  // Raised by MATCH_CLAIM (cash/matching.js). The claimer is excluded (four eyes).
  // Emailed: a waiting approval.
  n('CASH_MATCH_AWAITING_APPROVAL', {
    screen: 'Transactions & Approvals', link: 'approval', severity: 'ACTION', requiredClaims: ['core.cash.approve'],
    emailBecause: 'APPROVAL', defaultRoles: ['SUPERVISOR'],
  }),
  // Raised by MATCH_REJECT. Only the claimer (subject) is told. Emailed: a rejection.
  n('CASH_MATCH_REJECTED', {
    screen: 'Transactions & Approvals', link: 'approval', severity: 'WARNING', audience: 'SUBJECT', requiredClaims: ['core.cash.claim'],
    emailBecause: 'REJECTION',
  }),
  // Raised by the close run's checks step when unmatched money reaches ESCALATE or RETURN_DUE
  // (cash/receipts.js RECEIPT_STAGE). The checks only count, outside any transaction, and over every
  // fund at once - so it is a notifyNow message, with no fund. Repeated each weekday while any remain.
  n('RECEIPT_ESCALATED', {
    screen: 'Transactions & Approvals', link: 'approval', severity: 'WARNING', requiredClaims: ['core.cash.claim', 'core.payout.manage'],
    defaultRoles: ['OPERATOR', 'SUPERVISOR'], raisedBy: 'notifyNow',
  }),
  // Raised by the close run's checks step when payouts are pending (cash/payouts.js pendingPayouts),
  // one per fund - a read outside any transaction, so a notifyNow message.
  n('PAYOUTS_DUE', {
    screen: 'Transactions & Approvals', link: 'approval', severity: 'ACTION', requiredClaims: ['core.payout.manage'],
    defaultRoles: ['OPERATOR'], raisedBy: 'notifyNow',
  }),
  // Raised by cash/payouts.js returnPayoutItem: the bank refused the payment.
  // Emailed: counted as an automatic rejection.
  n('PAYOUT_RETURNED', {
    screen: 'Transactions & Approvals', link: 'approval', severity: 'WARNING', requiredClaims: ['core.payout.manage'],
    emailBecause: 'AUTO_REJECTION', defaultRoles: ['OPERATOR', 'SUPERVISOR'],
  }),

  // --- 9.6 Clients and Settings ------------------------------------------------
  // Raised by holders/manual.js proposeHolder. The proposer is excluded. Emailed: a waiting approval.
  n('HOLDER_AWAITING_APPROVAL', {
    screen: 'Clients', link: 'clients', severity: 'ACTION', requiredClaims: ['core.holder.approve'],
    emailBecause: 'APPROVAL', defaultRoles: ['SUPERVISOR'],
  }),
  // Raised by approveHolderRequest. The proposer only, in-app.
  n('HOLDER_REQUEST_APPROVED', {
    screen: 'Clients', link: 'clients', severity: 'INFO', audience: 'SUBJECT', requiredClaims: ['core.clients.view'],
  }),
  // Raised by rejectHolderRequest. The proposer only. Emailed: a rejection.
  n('HOLDER_REQUEST_REJECTED', {
    screen: 'Clients', link: 'clients', severity: 'WARNING', audience: 'SUBJECT', requiredClaims: ['core.clients.view'],
    emailBecause: 'REJECTION',
  }),
  // Raised by fees/proposals.js proposeFundFees. Only ADMIN holds core.fees.approve (IP-102),
  // so ADMIN is seeded. The proposer is excluded. Emailed: a waiting approval.
  n('FEE_CHANGE_AWAITING_APPROVAL', {
    screen: 'Settings', link: 'fundConfig', severity: 'ACTION', requiredClaims: ['core.fees.approve'],
    emailBecause: 'APPROVAL', defaultRoles: ['ADMIN'],
  }),
  // Raised by approveFundFees. The proposer only, in-app.
  n('FEE_CHANGE_APPROVED', {
    screen: 'Settings', link: 'fundConfig', severity: 'INFO', audience: 'SUBJECT', requiredClaims: ['core.fees.propose', 'core.fees.approve'],
  }),
  // Raised by rejectFundFees. The proposer only. Emailed: a rejection.
  n('FEE_CHANGE_REJECTED', {
    screen: 'Settings', link: 'fundConfig', severity: 'WARNING', audience: 'SUBJECT', requiredClaims: ['core.fees.propose', 'core.fees.approve'],
    emailBecause: 'REJECTION',
  }),

  // --- 9.7 Access and security -------------------------------------------------
  // Raised by authority/access.js inviteUser. No role is seeded (ADMIN gets only the very
  // important codes); an administrator can subscribe a role.
  n('USER_INVITED', {
    screen: 'Access', link: 'userManagement', severity: 'INFO', requiredClaims: ['core.access.admin'],
  }),
  // Raised by authority/access.js suspendUser. ADMIN seeded: a suspension is a security event.
  n('USER_SUSPENDED', {
    screen: 'Access', link: 'userManagement', severity: 'WARNING', requiredClaims: ['core.access.admin'], defaultRoles: ['ADMIN'],
  }),
  // Raised by authority/access.js reinstateUser. Not seeded.
  n('USER_REINSTATED', {
    screen: 'Access', link: 'userManagement', severity: 'INFO', requiredClaims: ['core.access.admin'],
  }),
  // Raised by setUserRoles, assignRole and removeRole. The person whose roles changed (the
  // subject) is told in-app; no role is seeded.
  n('USER_ACCESS_CHANGED', {
    screen: 'Access', link: 'userManagement', severity: 'INFO', audience: 'ROLES_AND_SUBJECT', requiredClaims: ['core.access.admin'],
  }),
  // Raised by saveRole and retireRole. Not seeded.
  n('ROLE_CHANGED', {
    screen: 'Access', link: 'userManagement', severity: 'INFO', requiredClaims: ['core.access.admin'],
  }),
  // Raised by authority/access.js resetMfa. The person whose authenticator was reset is ALWAYS
  // emailed (emailLocked) - they are the one who can say "that was not me". ADMIN seeded.
  n('MFA_RESET', {
    screen: 'Access', link: 'userManagement', severity: 'WARNING', audience: 'ROLES_AND_SUBJECT', requiredClaims: ['core.access.admin'],
    emailBecause: 'SECURITY', defaultRoles: ['ADMIN'],
  }),
  // Raised by auth/profile.js (change) and auth/password-reset.js (reset). Only the person, always
  // emailed, never mutable. No claims: it is about the person's own account.
  n('PASSWORD_CHANGED', {
    screen: 'Access', link: 'profile', severity: 'WARNING', audience: 'SUBJECT', requiredClaims: [], emailBecause: 'SECURITY',
  }),
  // Raised by authority/challenge.js recordFailure when the fifth wrong code locks step-up. That
  // state lives in DynamoDB, not the register, so it goes by notifyNow. The person is always emailed.
  n('STEP_UP_LOCKED', {
    screen: 'Access', link: 'userManagement', severity: 'WARNING', audience: 'ROLES_AND_SUBJECT', requiredClaims: ['core.access.admin'],
    emailBecause: 'SECURITY', defaultRoles: ['ADMIN'], raisedBy: 'notifyNow',
  }),
]);

// Lookup by code, built once. Every helper below reads it; nothing writes it.
const BY_CODE = new Map(NOTIFICATIONS.map((entry) => [entry.code, entry]));

/**
 * What each code means, in words an administrator choosing a role's
 * notifications, and a person deciding what to mute, can act on. Display
 * only, kept beside the codes so a new code cannot arrive without its words
 * (catalogue.test.js checks it), as authority/claims.js CLAIM_TEXT is.
 */
export const NOTIFICATION_TEXT = Object.freeze({
  INTAKE_FILE_AWAITING_ACCEPTANCE: { label: 'A file is waiting for acceptance', help: 'A checked file needs a second person to accept or reject it.' },
  INTAKE_FILE_QUARANTINED: { label: 'A file was quarantined', help: 'The malware scan or the file checks refused an uploaded file.' },
  INTAKE_FILE_REJECTED: { label: 'Your file was rejected', help: 'A checker rejected a file you uploaded.' },
  INTAKE_FILE_ACCEPTED: { label: 'Your file was accepted', help: 'A file you uploaded was accepted, by a checker or automatically.' },
  PRICE_RUN_AWAITING_APPROVAL: { label: 'A price is waiting for approval', help: 'A NAV run passed its checks and needs one approval to publish the price.' },
  PRICE_RUN_QUARANTINED: { label: 'A price failed its checks', help: 'A NAV run failed a tolerance check and needs an override or a rejection.' },
  PRICE_PUBLISHED: { label: 'A price was published', help: 'A fund\'s unit price for a date was approved and published.' },
  PRICE_RUN_REJECTED: { label: 'A price run was rejected', help: 'A NAV run was rejected; the fund must be priced again from a new file.' },
  ORDERS_AWAITING_REVIEW: { label: 'Transactions are waiting for review', help: 'New transactions were accepted into a dealing book and need a maker\'s review.' },
  ORDERS_AWAITING_APPROVAL: { label: 'Transactions are waiting for approval', help: 'Reviewed transactions are ready for a checker. Never sent to the people who reviewed them.' },
  ORDERS_REJECTED: { label: 'Transactions were rejected', help: 'Transactions were rejected - at intake, by a checker, from hold, or for a unit shortfall - and moved to Rejected transactions.' },
  ORDERS_SENT_BACK: { label: 'Transactions were sent back', help: 'A checker sent reviewed transactions back for another review.' },
  ORDER_HELD: { label: 'A transaction is on hold', help: 'A transaction was held at intake and needs releasing or rejecting.' },
  ORDERS_ROLLED_NOT_REVIEWED: { label: 'Transactions rolled without a review', help: 'Transactions missed their cut-off without a review and moved to the next dealing day.' },
  ORDERS_FROZEN_LATE: { label: 'Transactions were frozen late', help: 'Transactions were found in a book that had already closed.' },
  ORDERS_EXPIRED: { label: 'Transactions expired', help: 'Unfunded transactions lapsed.' },
  ALLOCATION_FAILED: { label: 'An allocation failed', help: 'Approval could not post a transaction\'s units.' },
  BOOK_CLOSED: { label: 'A book closed', help: 'A fund\'s dealing book closed, on schedule or by hand.' },
  BUSINESS_DAY_STAGE_CHANGED: { label: 'The business day moved on', help: 'The business day reached its next stage.' },
  BUSINESS_DAY_BLOCKED: { label: 'The business day is blocked', help: 'Day end is due, but something it waits for is still missing.' },
  BUSINESS_DAY_STEP_FAILED: { label: 'A business day step failed', help: 'One of the business day\'s automatic steps failed.' },
  FEEDS_FAILED: { label: 'Analytics feeds failed', help: "A day's analytics files did not reach the analytics platform; the notice names the cause." },
  FEEDS_STALE: { label: 'Analytics feeds are stale', help: "A day's analytics files no longer match the register and need sending again." },
  FEEDS_WAITING_FOR_WM: { label: 'Analytics feeds wait for WM holdings', help: "A day's analytics files are held until the wealth-managed holdings are uploaded, or the day is recorded as having none." },
  DAY_END_WAITING: { label: 'Day end is waiting', help: "Day end is due, but the day is not priced yet or some of its orders still need approving or rejecting; it tries again automatically." },
  DAY_END_STOPPED: { label: 'Day end stopped with issues', help: "Day end found client or fund figures that do not agree. The day stays open, no files are sent and the funds' prices wait; raise a ticket to engineering with the run id." },
  DAY_END_CLIENT_REBUILT: { label: 'Day end rebuilt client balances', help: "Day end found client balances out of step with their transactions and rebuilt them from the transactions; each change is recorded in the Day end issues." },
  DAY_END_STATEMENT_MISSING: { label: 'Day end found bank statements missing', help: 'The day closed without a bank statement for every collection account.' },
  STATEMENT_UNMATCHED_LINES: { label: 'Bank credits are unmatched', help: 'A statement loaded with credits the matcher could not match to a transaction.' },
  STATEMENT_REFUSED: { label: 'A bank statement was refused', help: 'A statement was refused on load: its chain is broken, it is a duplicate, or it is invalid.' },
  CASH_MATCH_AWAITING_APPROVAL: { label: 'A payment match is waiting for approval', help: 'Someone matched a bank credit to a transaction; a second person must approve it.' },
  CASH_MATCH_REJECTED: { label: 'Your payment match was rejected', help: 'A checker rejected a payment match you made.' },
  RECEIPT_ESCALATED: { label: 'Unmatched money is ageing', help: 'Money nobody could match reached its escalation or return deadline.' },
  PAYOUTS_DUE: { label: 'Payouts are due', help: 'Payouts are waiting to be generated for a business date.' },
  PAYOUT_RETURNED: { label: 'A payout was returned', help: 'The bank returned a payout.' },
  HOLDER_AWAITING_APPROVAL: { label: 'A holder is waiting for approval', help: 'A new holder was proposed and needs a second person.' },
  HOLDER_REQUEST_APPROVED: { label: 'Your holder proposal was approved', help: 'A holder you proposed was approved.' },
  HOLDER_REQUEST_REJECTED: { label: 'Your holder proposal was rejected', help: 'A holder you proposed was rejected.' },
  FEE_CHANGE_AWAITING_APPROVAL: { label: 'A fee change is waiting for approval', help: 'A fund\'s fees were proposed and need another person\'s approval.' },
  FEE_CHANGE_APPROVED: { label: 'Your fee change was approved', help: 'A fee change you proposed was approved.' },
  FEE_CHANGE_REJECTED: { label: 'Your fee change was rejected', help: 'A fee change you proposed was rejected.' },
  USER_INVITED: { label: 'A user was invited', help: 'An administrator invited someone to the Console.' },
  USER_SUSPENDED: { label: 'A user was suspended', help: 'An administrator suspended someone\'s access.' },
  USER_REINSTATED: { label: 'A user was reinstated', help: 'An administrator reinstated someone\'s access.' },
  USER_ACCESS_CHANGED: { label: 'Someone\'s roles changed', help: 'An administrator changed a person\'s roles. The person is told too.' },
  ROLE_CHANGED: { label: 'A role changed', help: 'A role\'s permissions or notifications were edited, or the role was retired.' },
  MFA_RESET: { label: 'An authenticator was reset', help: 'An administrator reset someone\'s authenticator. The person is always emailed.' },
  PASSWORD_CHANGED: { label: 'Your password changed', help: 'Your password was changed or reset. Always emailed.' },
  STEP_UP_LOCKED: { label: 'Step-up was locked', help: 'Five wrong authenticator codes locked someone\'s step-up. The person is always emailed.' },
});

/** The Console section each group of notifications belongs to, as the navigation names it. */
export const NOTIFICATION_GROUP_LABEL = Object.freeze({
  'File Uploads': 'File Uploads',
  'Unit Prices': 'Unit Prices',
  'Transactions & Approvals': 'Transaction Approval',
  'Business Day': 'Business Day',
  Clients: 'Clients',
  Settings: 'Settings',
  Access: 'User Management',
});

/** True for a code this build knows. */
export function isNotification(code) {
  return typeof code === 'string' && BY_CODE.has(code);
}

/** The entry for a code; a TypeError for one this build does not know - a programming error, never a request's. */
export function notificationOf(code) {
  const entry = typeof code === 'string' ? BY_CODE.get(code) : undefined;
  if (!entry) throw new TypeError('not a notification code: ' + String(code));
  return entry;
}

/** A code's words, with the code itself as the last resort. */
export function notificationText(code) {
  const text = NOTIFICATION_TEXT[code];
  return text ? { ...text } : { label: String(code), help: '' };
}

/** Whether a person may mute the code (section 5.2a): every code but the locked security notices. */
export function isMutable(code) {
  return !notificationOf(code).emailLocked;
}

/**
 * Whether a role holding these claims may subscribe to the code (section 3):
 * it must hold one of the code's required claims. A SUBJECT-only code is
 * never subscribed to.
 */
export function coveredBy(code, claims) {
  const entry = notificationOf(code);
  if (entry.audience === 'SUBJECT') return false;
  const held = new Set(claims ?? []);
  return entry.requiredClaims.some((claim) => held.has(claim));
}
